import React, { useCallback, useState } from 'react';
import { View, Text, ScrollView, TextInput, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import Toast from 'react-native-toast-message';

import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { addEventRegistrantCode, setEventRegistrantCodeActive } from '@/data/events';
import { showAlert } from '@/utils/showAlert';
import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import { ActivityIndicator } from 'react-native-paper';

/**
 * Sheet that lists the EventRegistrationCodes linked to a single registrant
 * and lets staff revoke / reactivate / add codes.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {string} eventInstanceID
 * @param {string} personID
 * @param {string} personName - "{firstName} {lastName}"
 */
const RegistrantCodesSheet = ({ isOpen, onClose, eventInstanceID, personID, personName }) => {
    const router = useRouter();
    const queryClient = useQueryClient();

    const [manualCode, setManualCode] = useState('');
    const [addCodeError, setAddCodeError] = useState(null);

    const { data: loggedInUser } = Query.people.loggedInUser();

    const { data: codes = [], isFetching } = Query.eventInstances.registrantCodes({
        eventInstanceID,
        personID,
        options: { enabled: !!isOpen && !!eventInstanceID && !!personID },
    });

    const invalidateCodes = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.registrantCodes(eventInstanceID, personID) });
    }, [queryClient, eventInstanceID, personID]);

    const toggleCodeMutation = useMutation({
        mutationFn: setEventRegistrantCodeActive,
        onSuccess: (_data, vars) => {
            invalidateCodes();
            Toast.show({ type: 'success', text2: vars.isActive ? 'Code reactivated.' : 'Code revoked.', position: 'top' });
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to update code.', position: 'top' });
        },
    });

    const addCodeMutation = useMutation({
        mutationFn: addEventRegistrantCode,
        onSuccess: () => {
            invalidateCodes();
            setManualCode('');
            setAddCodeError(null);
            Toast.show({ type: 'success', text2: 'Code linked successfully.', position: 'top' });
        },
        onError: (err) => {
            setAddCodeError(err?.message || 'Failed to link code.');
        },
    });

    const handleAddCode = useCallback(() => {
        const trimmed = manualCode.trim();
        if (!trimmed) return;
        setAddCodeError(null);
        addCodeMutation.mutate({
            eventInstanceID,
            personID,
            code: trimmed,
            createdByPersonID: loggedInUser?.id,
        });
    }, [manualCode, addCodeMutation, eventInstanceID, personID, loggedInUser?.id]);

    const handleScanQR = useCallback(() => {
        // Do NOT close the sheet — it stays mounted (hidden while unfocused via
        // SheetModal stayOpenOnBlur) so returning from the scanner restores it.
        router.push({
            pathname: `/events/instances/${eventInstanceID}/add-registration-code`,
            params: { eventInstanceID, personID, personName },
        });
    }, [router, eventInstanceID, personID, personName]);

    const handleToggle = useCallback((code) => {
        const isActive = !code.isActive;
        showAlert({
            title: isActive ? 'Reactivate Code' : 'Revoke Code',
            subtitle: isActive
                ? 'Reactivate this code so it can be scanned again for check-in?'
                : 'Revoke this code? It will no longer work for check-in until reactivated.',
            confirmText: isActive ? 'Reactivate' : 'Revoke',
            isDestructive: !isActive,
            onConfirm: () => toggleCodeMutation.mutate({ codeID: code.id, isActive }),
            onCancel: () => {},
        });
    }, [toggleCodeMutation]);

    const renderCode = useCallback((code) => {
        const createdBy = code.CreatedBy
            ? `${code.CreatedBy.firstName || ''} ${code.CreatedBy.lastName || ''}`.trim()
            : null;
        const createdDate = code.createdAt
            ? new Date(code.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
            : '';

        return (
            <View key={code.id} style={styles.codeCard} testID={`registrant-code-${code.id}`}>
                <View style={styles.codeMain}>
                    <Text style={styles.codeText} numberOfLines={1}>{code.code}</Text>
                    <View style={styles.codeMeta}>
                        <View style={[styles.statusChip, code.isActive ? styles.statusActive : styles.statusRevoked]}>
                            <Text style={[styles.statusChipText, code.isActive ? styles.statusActiveText : styles.statusRevokedText]}>
                                {code.isActive ? 'Active' : 'Revoked'}
                            </Text>
                        </View>
                        <Text style={styles.codeMetaText}>{createdDate}</Text>
                        {createdBy && <Text style={styles.codeMetaText}>· {createdBy}</Text>}
                    </View>
                </View>
                <SolidButton
                    size="sm"
                    type={code.isActive ? 'error' : 'success'}
                    variant="muted"
                    text={code.isActive ? 'Revoke' : 'Reactivate'}
                    testID={`code-toggle-${code.id}`}
                    processing={toggleCodeMutation.isPending && toggleCodeMutation.variables?.codeID === code.id}
                    onPress={() => handleToggle(code)}
                />
            </View>
        );
    }, [toggleCodeMutation, handleToggle]);

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title={personName ? `${personName} — Codes` : 'Codes'}
            cancelText="Close"
            stayOpenOnBlur
        >
            <View style={styles.container}>
                <View style={styles.addRow}>
                    <TextInput
                        style={styles.input}
                        placeholder="Enter code"
                        value={manualCode}
                        onChangeText={setManualCode}
                        autoCapitalize="none"
                        autoCorrect={false}
                        testID="manual-code-input"
                    />
                    <SolidButton
                        text="Add Code"
                        type="primary"
                        size="md"
                        testID="manual-link-code-button"
                        disabled={!manualCode.trim()}
                        processing={addCodeMutation.isPending}
                        onPress={handleAddCode}
                    />
                </View>
                {Platform.OS !== 'web' && (
                    <SolidButton
                        text="Scan QR Code"
                        type="secondary"
                        variant="outlined"
                        size="md"
                        iconName="qr-code"
                        testID="scan-qr-button"
                        onPress={handleScanQR}
                    />
                )}
                {addCodeError && <Text style={styles.errorText}>{addCodeError}</Text>}

                {isFetching && codes.length === 0 ? (
                    <View style={styles.loadingContainer}>
                        <ActivityIndicator />
                    </View>
                ) : codes.length === 0 ? (
                    <EmptyListPlaceholder message="No codes linked yet." />
                ) : (
                    <ScrollView style={styles.list} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
                        {codes.map(renderCode)}
                    </ScrollView>
                )}
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        padding: 16,
        gap: 12,
    },
    addRow: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'stretch',
    },
    input: {
        flex: 1,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 10,
        paddingHorizontal: 14,
        paddingVertical: 12,
        fontSize: 15,
        fontFamily: fonts.body,
        color: colors.text,
        backgroundColor: colors.surface,
    },
    errorText: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.error,
        textAlign: 'center',
    },
    loadingContainer: {
        paddingVertical: 24,
        alignItems: 'center',
    },
    list: {
        maxHeight: 420,
    },
    listContent: {
        gap: 8,
        paddingBottom: 8,
    },
    codeCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
    },
    codeMain: {
        flex: 1,
        gap: 6,
    },
    codeText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    codeMeta: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        flexWrap: 'wrap',
    },
    statusChip: {
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    statusActive: {
        backgroundColor: colors.successContainer,
    },
    statusRevoked: {
        backgroundColor: colors.errorContainer,
    },
    statusChipText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        textTransform: 'uppercase',
    },
    statusActiveText: {
        color: colors.success,
    },
    statusRevokedText: {
        color: colors.error,
    },
    codeMetaText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
}));

export default RegistrantCodesSheet;
